import React, { useState, useEffect } from 'react';

import { Button, Input, Text, FormAlert, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import api from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';

import type { IChangeProfileDialogProps } from './ChangeProfileDialog.type';

interface ProfileFormData {
  firstName: string;
  lastName: string;
}

export const ChangeProfilePanel: React.FC<IChangeProfileDialogProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user, refreshUserData } = useAuth();
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Initialize form data when modal opens or user data changes
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      });
    }
  }, [isOpen, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    
    // Validate first name against Keycloak pattern: letters, spaces, apostrophes, and hyphens only
    // Pattern: ^[\p{L}\s'-]+$ (no periods, numbers, or other special characters)
    const namePattern = /^[\p{L}\s'-]+$/u;
    if (!namePattern.test(formData.firstName.trim())) {
      setError('First name can only contain letters, spaces, apostrophes and hyphens (no periods or numbers)');
      return false;
    }
    
    if (!namePattern.test(formData.lastName.trim())) {
      setError('Last name can only contain letters, spaces, apostrophes and hyphens (no periods or numbers)');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    console.log('🚀 Starting profile update...', {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      currentUser: user
    });

    setLoading(true);
    setError('');

    try {
      console.log('📡 Sending API request to /auth/profile...');
      const response = await api.put('/auth/profile', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      });
      
      console.log('✅ Profile update API response:', response.data);
      setSuccess(true);
      
      console.log('🔄 Current user data before refresh:', user);
      console.log('🔄 Calling refreshUserData()...');
      
      // Refresh user data to update the display name immediately
      await refreshUserData();
      
      console.log('✅ User data refresh completed');
      
      setTimeout(() => {
        console.log('🚪 Closing panel after delay...');
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);
    } catch (err: any) {
      console.error('❌ Profile update failed:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Full error object:', {
        message: err.message,
        response: err.response,
        data: err.response?.data,
        detail: err.response?.data?.detail
      });
      
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' ? err.response.data : errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.error('❌ Final error message to display:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        // Reset form to original user data
        if (user) {
          setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || ''
          });
        }
        setError('');
        setSuccess(false);
        onClose?.();
      }, 300);
    }
  };

  const clearError = () => {
    if (error) setError('');
  };

  const hasChanges = user && (
    formData.firstName !== (user.firstName || '') ||
    formData.lastName !== (user.lastName || '')
  );

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Update Profile</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
            {/* Success Message */}
            {success && (
              <FormAlert
                message="Profile updated successfully!"
                variant="success"
              />
            )}

            {/* Error Message */}
            {error && (
              <FormAlert
                message={error}
                variant="error"
                onDismiss={clearError}
              />
            )}

            <Text variant="text-sm" color="secondary" as="p" className="w-full">
              Update your profile information below.
            </Text>

            <div className="w-full space-y-6">
              {/* First Name */}
              <Input
                id="firstName"
                name="firstName"
                type="text"
                label="First Name"
                placeholder="Enter your first name"
                required
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={loading || success}
                autoFocus
                className="w-full"
              />

              {/* Last Name */}
              <Input
                id="lastName"
                name="lastName"
                type="text"
                label="Last Name"
                placeholder="Enter your last name"
                required
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={loading || success}
                className="w-full"
              />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Updating...' : 'Update Profile'}
              disabled={loading || success || !hasChanges}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

